import getConfig from 'next/config';

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import FlexBox from './FlexBox';
import SymbolButton from './SymbolButton';
import { getLabel } from '../utils/i18n';

import withErrorHandler from '../utils/withErrorHandler';
import { formatNumber } from '../utils/number';
import zIndexScale from '../utils/zIndexScale';
import FiatPrice from './FiatPrice';
import LazyImg from './LazyImg';

const {
  publicRuntimeConfig: { assetPrefix },
} = getConfig();

const MarketsBox = styled(FlexBox)`
  width: 100%;
  > a {
    flex: 1;
    margin-right: 20px;
    :last-of-type {
      margin: 0;
    }
  }
  & a:active {
    text-decoration: none;
  }
`;
const MarketItem = styled.div`
  display: table;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  line-height: 100%;
  padding: 15px;
  width: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.fontColor};
  cursor: pointer;
`;

const MarketItemInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  position: relative;
  z-index: ${zIndexScale.HotMarkets};
`;

const PairBox = styled(FlexBox)`
  margin-bottom: 0;
`;

const Pair = styled.span`
  font-size: ${({ theme }) => theme.fontSizeMd};
`;

const Amount = styled.span`
  font-size: 105%;
`;

const GreenAmount = styled(Amount)`
  color: ${({ theme }) => theme.up};
`;

const GreenAmountPlus = styled(Amount)`
  color: ${({ theme }) => theme.up};
  :before {
    content: '+';
  }
`;

const RedAmount = styled(Amount)`
  color: ${({ theme }) => theme.down};
`;

const PriceBox = styled(FlexBox)`
  padding: 14px 0 12px;
  /* width: 200px; */
`;

const DollarPrice = styled(FlexBox)`
  font-family: ${({ theme }) => theme.fontIBMMedium};
  font-weight: 500;
  color: rgba(132,142,156,1);
  line-height: 15px;
}
`;

const VolumeBox = styled(FlexBox)`
  > span {
    font-weight: bold;
  }
`;

const Price = styled(Pair)`
  margin-right: 3px;
  font-size: 22px;
  font-weight: bold;
  font-family: ${({ theme }) => theme.fontIBMBold};
`;
const Volume = styled.div`
  font-size: 12px;
  line-height: 13px;
  margin-left: 10px;
  color: #848e9c;
`;

const AreaBox = styled.div`
  height: 28px;
  width: 86px;
  position: absolute;
  right: 20px;
  top: 50%;
  margin-top: -14px;
  border-bottom: 1px dashed #f0b90a;
  > svg {
    width: 100%;
    height: 100%;
  }
`;

const mapStateToProps = ({
  locale,
  tickers,
  fiatRate,
  currencyList,
  userPreferredCurrency,
}) => ({
  locale,
  tickers,
  fiatRate,
  currencyList,
  userPreferredCurrency,
});

type Ticker = {
  s: string,
  ba: string,
  qa: string,
  c: number,
  o: number,
  q: number,
  tickSize: number,
};

type Tickers = { [string]: Ticker };

type Props = {
  i18n: Object,
  locale: string,
  tickers: Tickers,
  recommends: Array<Object>,
  userPreferredCurrency: Object,
  fiatRate: Object,
  currencyList: Array<Object>,
};

export default
@withErrorHandler
@connect(mapStateToProps)
class HotMarkets extends React.Component<Props> {
  prevTickers = {};

  constructor(props: Props) {
    super(props);
    this.prevTickers = this.props.tickers;
  }

  shouldComponentUpdate(nextProps: Props) {
    return nextProps.tickers !== this.props.tickers;
  }

  componentDidUpdate() {
    this._setPrevTickers();
  }

  _setPrevTickers = () => {
    const { tickers } = this.props;
    this.prevTickers = tickers;
  };

  _renderChange(c: number, o: number) {
    if (!Number(o)) return <span>–</span>;
    if (c > o) {
      return <GreenAmountPlus>{formatNumber(((c - o) / o) * 100, 2)}%</GreenAmountPlus>;
    }
    if (c === o) {
      return <span>0.00%</span>;
    }
    return <RedAmount>{formatNumber(((c - o) / o) * 100, 2)}%</RedAmount>;
  }

  _renderPrice(current: number, last: number, tickSize: number) {
    if (current > last) {
      return <GreenAmount>{formatNumber(current, Math.abs(Math.log10(tickSize)))}</GreenAmount>;
    }
    if (current === last) {
      return <span>{formatNumber(current, Math.abs(Math.log10(tickSize)))}</span>;
    }
    return <RedAmount>{formatNumber(current, Math.abs(Math.log10(tickSize)))}</RedAmount>;
  }

  _getRecommends(tickers: Tickers, recommends: Array<Object>): Array<Ticker> {
    const arr = [];
    recommends.map(item => {
      Object.values(tickers).map((v: Ticker | any) => {
        if (item.asset === v.s) {
          arr.push(v);
        }
        return null;
      });
      return null;
    });
    return arr;
  }

  render() {
    const {
      i18n, locale, tickers, recommends,
    } = this.props;
    const arr = this._getRecommends(tickers, recommends);
    arr.length = 4;
    return (
      <MarketsBox justify="space-between">
        {arr.map(({
          s, ba, qa, c, o, q, tickSize,
        }: Ticker) => (
          <SymbolButton key={s} market={`${ba}_${qa}`} locale={locale}>
            <MarketItem className="market-item">
              <MarketItemInner>
                <PairBox justify="space-between">
                  <Pair>
                    {ba} / {qa}
                  </Pair>
                </PairBox>

                <PriceBox>
                  <Price>{this._renderPrice(c, this.prevTickers[s] ?.c, tickSize)}</Price>
                  <DollarPrice>
                    <FiatPrice prefix="/ " price={c} quoteAsset={qa} precision={2} color="#848E9C" />
                  </DollarPrice>
                </PriceBox>

                <VolumeBox>
                  {this._renderChange(+c, +o)}
                  <Volume>
                    {getLabel('exchange-home-tradeVolume', i18n)} {formatNumber(q, 2)} {qa}
                  </Volume>
                </VolumeBox>
              </MarketItemInner>
              <AreaBox>
                <LazyImg
                  src={`${assetPrefix}/kline/${s}.svg`}
                  alt={s}
                  width="86"
                  height="28"
                />
              </AreaBox>
            </MarketItem>
          </SymbolButton>
        ))}
      </MarketsBox>
    );
  }
}
